
import { 
  Card, 
  CardContent,
  CardFooter
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tag, ArrowRight, Package } from "lucide-react";

interface BundlingOpportunityProps {
  bundle: {
    id: number;
    name: string;
    description: string;
    includedItems: string[];
    regularPrice: number;
    bundlePrice: number;
    discount: number;
    ingredients: string[];
    overstock: string[];
  };
}

export const BundlingOpportunity = ({ bundle }: BundlingOpportunityProps) => {
  return (
    <Card className="overflow-hidden h-full">
      <div className="h-2 bg-indigo-500" />
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-lg">{bundle.name}</h3>
              <p className="text-muted-foreground text-sm">{bundle.description}</p>
            </div>
            <Badge className="bg-indigo-500">
              <Tag className="mr-1 h-3 w-3" />
              {bundle.discount.toFixed(1)}% off
            </Badge>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium">Included Items:</p>
            <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
              {bundle.includedItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Regular Price</p>
              <p className="font-semibold line-through text-muted-foreground">${bundle.regularPrice.toFixed(2)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Bundle Price</p>
              <p className="font-semibold text-indigo-600">${bundle.bundlePrice.toFixed(2)}</p>
            </div>
          </div>

          {bundle.overstock.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium flex items-center">
                <Package className="h-3 w-3 mr-1" />
                Uses Overstock Items:
              </p>
              <div className="flex flex-wrap gap-1">
                {bundle.overstock.map((item, index) => (
                  <Badge key={index} variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="bg-muted/30 p-3">
        <Button variant="outline" size="sm" className="w-full">
          Implement Bundle <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};
